import WEAPONS from "../../consts/WEAPONS";
import WeaponType from "../../enums/WeaponType";
import Game from "../Game";
import GameCore from "../GameCore";
import Enemy from "../enemy/Enemy";
import { GridSizer } from "phaser3-rex-plugins/templates/ui/ui-components";
import { XP_BAR_HEIGHT } from "./PlayerUI";
import FormationPointer from "./FormationPointer";
import ApiService from "../../../handlers/apiService";
import { BoostTypeEnum } from "../../../handlers/types";
import { TEXT_STYLES } from "../../../utils/FontManager";
import StageService from "../../../services/StageService";
import { DEFAULT_SCALE } from "../../consts/UI";
import { showBoostPurchaseDialog } from "../../../utils/modal/ModalManager";

import {
  SPRITESHEET_KEYS,
  SPRITESHEET_FRAMES,
} from "../../consts/SPRITESHEETS";

window["Percent"] = Phaser.Math.Percent;

export interface WeaponIcon {
  weaponType: WeaponType;
  level: number;
  isWeapon: boolean;
  image?: Phaser.GameObjects.Image;
}

const scaleRatio = DEFAULT_SCALE;
// const scaleRatio = Math.min(2, devicePixelRatio)

export const UI_PADDING = 16 * scaleRatio;
export const TOP_MARGIN = 112 * scaleRatio;
export const HEADER_HEIGHT = 60 * scaleRatio; // --header-height
export const BORDER_RADIUS = 8 * scaleRatio; // --border-radius
export const GAP = 4 * scaleRatio;
export const ICON_SIZE = 24 * scaleRatio;

export default class MainUi {
  scene: Phaser.Scene;
  SurvivedSecondsText: Phaser.GameObjects.Text;
  KillsText: Phaser.GameObjects.Text;
  PlayerCoinsText: Phaser.GameObjects.Text;
  sizer: GridSizer;
  WeaponIcons: WeaponIcon[];
  PowerUpIcons: WeaponIcon[];
  private formationPointer: FormationPointer;
  private explosionBoostIcon: Phaser.GameObjects.Image;
  private explosionBoostCount: Phaser.GameObjects.Text;
  private healBoostIcon: Phaser.GameObjects.Image;
  private healBoostCount: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.WeaponIcons = new Array();
    this.PowerUpIcons = new Array();

    const width = this.scene.renderer.width - UI_PADDING * 2;
    const x = this.scene.renderer.width / 2;

    // Create backdrop with rounded corners
    const backdrop = this.scene.add.graphics();
    backdrop.setDepth(GameCore.ZInGameUI - 4).fillStyle(0x202a38, 0.8); // --bg-secondary-color

    // Draw rounded rectangle
    backdrop.fillRoundedRect(
      x - width / 2,
      TOP_MARGIN,
      width,
      HEADER_HEIGHT,
      BORDER_RADIUS
    );
    backdrop.setScrollFactor(0);

    // Create explosion boost button
    this.createExplosionBoostButton();
    this.createHealBoostButton();
    this.createPauseButton();
    this.updateBoostCounts();

    this.SurvivedSecondsText = this.scene.add
      .text(
        0.5 * this.scene.renderer.width,
        TOP_MARGIN + XP_BAR_HEIGHT + HEADER_HEIGHT / 2 - GAP * 3,
        "",
        {
          ...TEXT_STYLES.PIXEL,
          fontSize: `${12 * scaleRatio}px`,
          color: "white",
        }
      )
      .setScrollFactor(0)
      .setOrigin(0.5, 0)
      .setDepth(GameCore.ZInGameUI - 3);

    // COIN COUNTER
    this.PlayerCoinsText = this.scene.add
      .text(
        this.scene.renderer.width - UI_PADDING * 3 - GAP,
        TOP_MARGIN + XP_BAR_HEIGHT + GAP * 5,
        "9999",
        {
          ...TEXT_STYLES.PIXEL,
          fontSize: `${10 * scaleRatio}px`,
          color: "white",
        }
      )
      .setScrollFactor(0)
      .setOrigin(1, 0.5)
      .setDepth(GameCore.ZInGameUI - 3);
    this.scene.add
      .image(0, 0, SPRITESHEET_KEYS.PICKUPS, SPRITESHEET_FRAMES.PICKUPS.COIN)
      .setSize(ICON_SIZE, ICON_SIZE)
      .setPosition(
        this.scene.renderer.width - UI_PADDING * 3,
        this.PlayerCoinsText.y + GAP
      )
      .setScrollFactor(0)
      .setOrigin(0, 0.5)
      .setDepth(GameCore.ZInGameUI - 3);

    // KILLS COUNTER
    this.KillsText = this.scene.add
      .text(
        this.scene.renderer.width - UI_PADDING * 3 - GAP,
        this.PlayerCoinsText.y + this.PlayerCoinsText.height + GAP,
        "9999",
        {
          ...TEXT_STYLES.PIXEL,
          fontSize: `${10 * scaleRatio}px`,
          color: "white",
        }
      )
      .setScrollFactor(0)
      .setOrigin(1, 0.5)
      .setDepth(GameCore.ZInGameUI - 3);
    this.scene.add
      .image(0, 0, SPRITESHEET_KEYS.PICKUPS, SPRITESHEET_FRAMES.PICKUPS.SKULL)
      .setSize(ICON_SIZE, ICON_SIZE)
      .setPosition(
        this.scene.renderer.width - UI_PADDING * 3,
        this.KillsText.y + GAP
      )
      .setScrollFactor(0)
      .setOrigin(0, 0.5)
      .setDepth(GameCore.ZInGameUI - 3);

    // WEAPONS AND POWERUPS UI
    this.sizer = this.scene.rexUI.add
      .gridSizer({
        anchor: {
          left: `left+${UI_PADDING * 2}`,
          top: `top+${TOP_MARGIN + XP_BAR_HEIGHT + GAP * 2}`,
        },
        width: (HEADER_HEIGHT - XP_BAR_HEIGHT - GAP * 3 + GAP) * 3,
        height: HEADER_HEIGHT - XP_BAR_HEIGHT - GAP * 3,
        column: 6,
        row: 2,
        columnProportions: 1,
        rowProportions: 1,
        space: {
          column: 4,
          row: 4,
        },

        createCellContainerCallback: function (scene, x, y, config) {
          config.expand = true;
          return scene.rexUI.add
            .roundRectangle(0, 0, 0, 0, 5, 0xaaaaaa)
            .setAlpha(0.5);
        },
      })
      .setScrollFactor(0)
      .setDepth(GameCore.ZInGameUI - 2)
      .layout();

    this.formationPointer = new FormationPointer(scene);
  }

  private createExplosionBoostButton() {
    const buttonSize = HEADER_HEIGHT;
    const buttonX = UI_PADDING;
    const buttonY = TOP_MARGIN + HEADER_HEIGHT + GAP * 2;

    // Create square background with rounded corners
    const background = this.scene.rexUI.add
      .roundRectangle(
        buttonX,
        buttonY,
        buttonSize,
        buttonSize,
        9 * scaleRatio,
        0x202a38
      )
      .setAlpha(0.8)
      .setDepth(GameCore.ZInGameUI + 1)
      .setScrollFactor(0)
      .setOrigin(0, 0)
      .setInteractive({ cursor: "pointer" });

    this.scene.rexUI.add
      .click(background, {
        mode: "release",
        clickInterval: 1000,
      })
      .on("click", () => {
        this.handleExplosionBoostClick();
      });

    // Create centered icon
    this.explosionBoostIcon = this.scene.add
      .image(
        buttonX + buttonSize / 2,
        buttonY + buttonSize / 2,
        "explosion_icon"
      )
      .setDisplaySize(69 * scaleRatio, 69 * scaleRatio)
      .setOrigin(0.5)
      .setDepth(GameCore.ZInGameUI + 2)
      .setScrollFactor(0);

    // Create count text in bottom right corner
    this.explosionBoostCount = this.scene.add
      .text(
        buttonX + buttonSize - 0 * scaleRatio,
        buttonY + buttonSize - 0 * scaleRatio,
        "0",
        {
          ...TEXT_STYLES.PIXEL,
          fontSize: `${12 * scaleRatio}px`,
          color: "#ffffff",
          padding: { x: 6, y: 3 },
        }
      )
      .setOrigin(1, 1)
      .setDepth(GameCore.ZInGameUI + 2)
      .setScrollFactor(0);
  }

  private createHealBoostButton() {
    const buttonSize = HEADER_HEIGHT;
    const buttonX = UI_PADDING;
    const buttonY = TOP_MARGIN + HEADER_HEIGHT + GAP * 2 + buttonSize + GAP * 2; // Position below explosion button

    // Create square background with rounded corners
    const background = this.scene.rexUI.add
      .roundRectangle(
        buttonX,
        buttonY,
        buttonSize,
        buttonSize,
        9 * scaleRatio,
        0x202a38
      )
      .setAlpha(0.8)
      .setDepth(GameCore.ZInGameUI + 1)
      .setScrollFactor(0)
      .setOrigin(0, 0)
      .setInteractive({ cursor: "pointer" });

    this.scene.rexUI.add
      .click(background, {
        mode: "release",
        clickInterval: 1000,
      })
      .on("click", () => {
        this.handleHealBoostClick();
      });

    // Create centered icon
    this.healBoostIcon = this.scene.add
      .image(buttonX + buttonSize / 2, buttonY + buttonSize / 2, "heal_icon")
      .setDisplaySize(61 * scaleRatio, 61 * scaleRatio)
      .setOrigin(0.5)
      .setDepth(GameCore.ZInGameUI + 2)
      .setScrollFactor(0);

    // Create count text in bottom right corner
    this.healBoostCount = this.scene.add
      .text(
        buttonX + buttonSize - 0 * scaleRatio,
        buttonY + buttonSize - 0 * scaleRatio,
        "0",
        {
          ...TEXT_STYLES.PIXEL,
          fontSize: `${12 * scaleRatio}px`,
          color: "#ffffff",
          padding: { x: 6, y: 3 },
        }
      )
      .setOrigin(1, 1)
      .setDepth(GameCore.ZInGameUI + 2)
      .setScrollFactor(0);
  }

  private createPauseButton() {
    const buttonSize = HEADER_HEIGHT;
    const buttonX = this.scene.renderer.width - UI_PADDING - buttonSize; // Right alignment
    const buttonY = TOP_MARGIN + HEADER_HEIGHT + GAP * 2;

    // Create square background with rounded corners
    const background = this.scene.rexUI.add
      .roundRectangle(
        buttonX,
        buttonY,
        buttonSize,
        buttonSize,
        9 * scaleRatio,
        0x202a38
      )
      .setAlpha(0.8)
      .setDepth(GameCore.ZInGameUI + 1)
      .setScrollFactor(0)
      .setOrigin(0, 0)
      .setInteractive({ cursor: "pointer" });

    this.scene.rexUI.add
      .click(background, {
        mode: "release",
        clickInterval: 1000,
      })
      .on("click", () => {
        Game.Core.SceneManager.PauseGame();
      });

    // Create pause icon (two vertical rectangles)
    const iconContainer = this.scene.add
      .container(buttonX + buttonSize / 2, buttonY + buttonSize / 2)
      .setDepth(GameCore.ZInGameUI + 2)
      .setScrollFactor(0);

    const barWidth = 8 * scaleRatio;
    const barHeight = buttonSize * 0.5;
    const barGap = 8 * scaleRatio;

    const leftBar = this.scene.add.rectangle(
      -barGap / 2 - barWidth / 2,
      0,
      barWidth,
      barHeight,
      0xffffff
    );

    const rightBar = this.scene.add.rectangle(
      barGap / 2 + barWidth / 2,
      0,
      barWidth,
      barHeight,
      0xffffff
    );

    iconContainer.add([leftBar, rightBar]);
  }

  private async handleExplosionBoostClick() {
    // Get current explosion count from UI
    const count = parseInt(this.explosionBoostCount.text || "0");

    if (count > 0) {
      // If we have explosions available, immediately use it
      this.triggerExplosion();

      // Optimistic update
      const newCount = count - 1;
      if (newCount === 0) {
        this.explosionBoostCount.setText("0").setVisible(false);
      } else {
        this.explosionBoostCount.setText(`${newCount}`);
      }

      // Then send request about usage
      try {
        await ApiService.getInstance().api.boostsUseCreate({
          boostType: BoostTypeEnum.Explosion,
          runId: StageService.getInstance().getRunId(),
        });

        // Wait for 1 second after updating the boost
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Update count after usage
        await this.updateBoostCounts();
      } catch (error) {
        console.error("Failed to record explosion usage:", error);
      }
    } else {
      // Show the boost purchase dialog
      showBoostPurchaseDialog(BoostTypeEnum.Explosion, async () => {
        // On purchase success, trigger explosion and update counts
        this.triggerExplosion();

        // Wait for 1 second before using newly bought boost
        await new Promise((resolve) => setTimeout(resolve, 1000));

        await ApiService.getInstance().api.boostsUseCreate({
          boostType: BoostTypeEnum.Explosion,
          runId: StageService.getInstance().getRunId(),
        });

        // Wait for 1 second before updating the boost
        await new Promise((resolve) => setTimeout(resolve, 1000));

        await this.updateBoostCounts();
      });
    }
  }

  private async handleHealBoostClick() {
    // Get current heal count from UI
    const count = parseInt(this.healBoostCount.text || "0");

    if (count > 0) {
      // If we have heals available, immediately use it
      this.triggerHeal();

      // Optimistic update
      const newCount = count - 1;
      if (newCount === 0) {
        this.healBoostCount.setText("0").setVisible(false);
      } else {
        this.healBoostCount.setText(`${newCount}`);
      }

      // Then send request about usage
      try {
        await ApiService.getInstance().api.boostsUseCreate({
          boostType: BoostTypeEnum.Heal,
          runId: StageService.getInstance().getRunId(),
        });

        // Wait for 1 second after updating the boost
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Update count after usage
        await this.updateBoostCounts();
      } catch (error) {
        console.error("Failed to record heal usage:", error);
      }
    } else {
      // Show the boost purchase dialog
      showBoostPurchaseDialog(BoostTypeEnum.Heal, async () => {
        // On purchase success, trigger heal and update counts
        this.triggerHeal();

        // Wait for 1 second before using newly bought boost
        await new Promise((resolve) => setTimeout(resolve, 1000));

        await ApiService.getInstance().api.boostsUseCreate({
          boostType: BoostTypeEnum.Heal,
          runId: StageService.getInstance().getRunId(),
        });

        // Wait for 1 second before updating the boost
        await new Promise((resolve) => setTimeout(resolve, 1000));

        await this.updateBoostCounts();
      });
    }
  }

  private triggerExplosion() {
    // Play explosion sound using pooled audio (sound settings handled by facade)
    Game.Core.poolManager?.playExplodeSound();

    const explosion = this.scene.add.sprite(
      Game.Core.Player.x,
      Game.Core.Player.y,
      "weapon_effects",
      "explosion_0"
    );
    explosion.setDepth(GameCore.ZInGameUI - 1);
    explosion.play("explosion_animation");

    // Scale up the blast to screen size
    const targetScale = Game.Core.scene.cameras.main.height / explosion.height;

    // Create the explosion animation
    Game.Core.scene.tweens.add({
      targets: explosion,
      scale: targetScale * 4,
      alpha: { from: 0.9, to: 0 },
      duration: 2000,
      ease: "Linear",
      onComplete: () => {
        explosion.destroy();
      },
    });

    // Get all enemies in the scene using physics system
    const enemies = Game.Core.scene.children.list.filter(
      (obj) => obj instanceof Enemy
    ) as Enemy[];

    // Apply explosion damage to all enemies
    enemies.forEach((enemy) => {
      enemy.GetExploded();
    });
  }

  private triggerHeal() {
    // Play heal sound using pooled audio (sound settings handled by facade)
    Game.Core.poolManager?.playAppleSound();

    // Heal the player
    Game.Core.Player.RecoverHp(100);
  }

  public async updateBoostCounts() {
    try {
      const response = await ApiService.getInstance().api.purchasesList();
      // Find explosion boost in the boosts array
      const explosionBoost = response.boosts.find(
        (boost) => boost.type === BoostTypeEnum.Explosion
      );
      const explosionBoostsCount = explosionBoost?.count || 0;

      // Hide the counter text if count is 0, otherwise show the count
      if (explosionBoostsCount === 0) {
        this.explosionBoostCount.setVisible(false);
      } else {
        this.explosionBoostCount.setVisible(true);
        this.explosionBoostCount.setText(explosionBoostsCount.toString());
      }

      // Find heal boost in the boosts array
      const healBoost = response.boosts.find(
        (boost) => boost.type === BoostTypeEnum.Heal
      );
      const healBoostsCount = healBoost?.count || 0;

      // Hide the counter text if count is 0, otherwise show the count
      if (healBoostsCount === 0) {
        this.healBoostCount.setVisible(false);
      } else {
        this.healBoostCount.setVisible(true);
        this.healBoostCount.setText(healBoostsCount.toString());
      }
    } catch (error) {
      console.error("Failed to update boost counts:", error);
    }
  }

  SetRemainingSeconds(remainingSeconds: number | null) {
    this.SurvivedSecondsText.setText(
      remainingSeconds === null ? "" : MainUi.FormatTime(remainingSeconds)
    );
  }

  UpdateCoins() {
    this.PlayerCoinsText.setText(
      Game.Core.PlayerOptions.RunCoins.toLocaleString()
    );
  }

  UpdateKills() {
    this.KillsText.setText(Game.Core.PlayerOptions.RunEnemies.toLocaleString());
  }

  static FormatTime(survivedSeconds: number) {
    return `${Math.floor(survivedSeconds / 60)}:${(survivedSeconds % 60)
      .toString()
      .padStart(2, "0")}`;
  }

  AddWeaponIcon(bulletType: WeaponType) {
    this.Add(bulletType);
  }

  RemoveWeaponIcon(bulletType: WeaponType) {
    let weaponIndex = this.WeaponIcons.findIndex(
      (w) => w.weaponType === bulletType
    );
    if (weaponIndex !== -1) {
      // Remove from UI grid first (if it exists in the grid)
      if (this.sizer) {
        this.sizer.removeAt(weaponIndex, 0);
      }

      // Remove from internal array and destroy the image
      const removedWeapon = this.WeaponIcons.splice(weaponIndex, 1)[0];
      if (removedWeapon.image) {
        removedWeapon.image.destroy(); // Destroy the image
      }
    } else {
      // Check in power-up icons
      weaponIndex = this.PowerUpIcons.findIndex(
        (w) => w.weaponType === bulletType
      );

      if (weaponIndex !== -1) {
        // For power-ups, the index in the grid is offset by the number of weapon icons
        if (this.sizer) {
          this.sizer.removeAt(weaponIndex, 1);
        }

        // Remove from internal array and destroy the image
        const removedPowerUp = this.PowerUpIcons.splice(weaponIndex, 1)[0];
        if (removedPowerUp.image) {
          removedPowerUp.image.destroy(); // Destroy the image
        }
      }
    }

    // Update the UI layout
    this.RearrangeIcons();
  }

  // Helper function to check if an icon is in any spritesheet
  private getSpritesheetInfo(
    iconName: string
  ): { key: string; frame: string } | null {
    // Check WEAPONS spritesheet
    const weaponFrames = Object.values(SPRITESHEET_FRAMES.WEAPONS);
    if (weaponFrames.includes(iconName as any)) {
      return { key: SPRITESHEET_KEYS.WEAPONS, frame: iconName };
    }

    // Check STATS spritesheet
    const statFrames = Object.values(SPRITESHEET_FRAMES.STATS);
    if (statFrames.includes(iconName as any)) {
      return { key: SPRITESHEET_KEYS.STATS, frame: iconName };
    }

    // Check PICKUPS spritesheet
    const pickupFrames = Object.values(SPRITESHEET_FRAMES.PICKUPS);
    if (pickupFrames.includes(iconName as any)) {
      return { key: SPRITESHEET_KEYS.PICKUPS, frame: iconName };
    }

    return null;
  }

  // Helper function to create an image with proper texture handling
  private createWeaponImage(iconName: string): Phaser.GameObjects.Image {
    const spritesheetInfo = this.getSpritesheetInfo(iconName);
    if (spritesheetInfo) {
      return this.scene.add.image(
        0,
        0,
        spritesheetInfo.key,
        spritesheetInfo.frame
      );
    } else {
      // Fall back to individual texture for non-spritesheet icons
      return this.scene.add.image(0, 0, iconName);
    }
  }

  Add(bulletType: WeaponType) {
    const weaponData = WEAPONS[bulletType][0];
    const isWeapon = !(weaponData.isPowerUp || this.WeaponIcons.length >= 6);
    let theWeapon: WeaponIcon | undefined;
    if (!isWeapon) {
      theWeapon = this.PowerUpIcons.find((t) => t.weaponType == bulletType);
    } else {
      theWeapon = this.WeaponIcons.find((t) => t.weaponType == bulletType);
    }
    if (theWeapon) {
      theWeapon.level++;
    } else {
      theWeapon = {
        weaponType: bulletType,
        level: 1,
        isWeapon: isWeapon,
        image: this.createWeaponImage(weaponData.iconName),
      };
      if (theWeapon.isWeapon) {
        this.WeaponIcons.push(theWeapon);
      } else {
        this.PowerUpIcons.push(theWeapon);
      }
    }
    this.RearrangeIcons();
  }
  RearrangeIcons() {
    this.WeaponIcons.forEach((value, index) => {
      const image = value.image;
      if (image) {
        this.sizer.removeAt(index, 0);
        this.sizer.add(
          this.scene.rexUI.add
            .badgeLabel({
              main: image,
              rightBottom: this.scene.add.text(0, 0, `${value.level}`, {
                ...TEXT_STYLES.REGULAR,
                fontSize: `${10 * scaleRatio}px`,
                color: "white",
                backgroundColor: "#202A38",
              }),
            })
            .setDepth(GameCore.ZInGameUI - 1)
            .layout(),
          {
            column: index,
            row: 0,
          }
        );
      }
    });

    this.PowerUpIcons.forEach((value, index) => {
      const image = value.image;
      if (image) {
        this.sizer.removeAt(index, 1);
        this.sizer.add(
          this.scene.rexUI.add
            .badgeLabel({
              main: image,
              rightBottom: this.scene.add.text(0, 0, `${value.level}`, {
                ...TEXT_STYLES.REGULAR,
                fontSize: `${10 * scaleRatio}px`,
                color: "white",
                backgroundColor: "#202A38",
              }),
            })
            .setDepth(GameCore.ZInGameUI - 1)
            .layout(),
          {
            column: index,
            row: 1,
          }
        );
      }
    });

    this.sizer.layout();
  }

  update() {
    this.formationPointer.update();
  }
}
